import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { List, Map } from 'immutable';

import AgGridTable from '../helpers/AgGridTable';
import { ContextMenuCellRenderer } from '../helpers/ContextMenuCellRenderer';

import style from './style.module.css';

// Tag type
const BROAD_INDUSTRY_CATEGORY = 6;

const gridStyle = { height: '100%' };
const sortingOrder = ['asc', 'desc'];
const rowClassRules = { [style.boldRow]: params => params.data.tagTypeId === BROAD_INDUSTRY_CATEGORY };

const COLUMNS = [
  { headerName: 'Tag Name', field: 'category', sortKey: 'category', width: 300 },
  { headerName: 'Parent Category', field: 'parent.category', sortKey: 'parent', width: 200 },
  { headerName: 'Tag Type', field: 'tagType.type', sortKey: 'tagType', width: 180 },
  { headerName: 'Targets', field: 'targetCount', sortKey: 'targetCount', width: 80 },
  { headerName: 'Buyers', field: 'buyerCount', sortKey: 'buyerCount', width: 80 },
  { headerName: 'Execs', field: 'contactCount', sortKey: 'contactCount', width: 80 },
  { headerName: 'Leads', field: 'leadCount', sortKey: 'leadsCount', width: 80 },
];

const CONTEXT_ACTIONS = List([
  Map({
    key: 'edit',
    name: (
      <>
        <i className="fa fa-pencil" /> Edit
      </>
    ),
  }),
  Map({
    key: 'delete',
    name: (
      <>
        <i className="fa fa-remove" /> Delete
      </>
    ),
  }),
]);

const TagTableComponent = ({ loading, onDelete, onClick, onDeleteParent, onEdit, tags, isSuper }) => {
  const columnDefs = useMemo(
    () =>
      List(
        COLUMNS.map(column => ({
          ...column,
          sortingOrder,
          ...(isSuper ? { cellRendererFramework: ContextMenuCellRenderer } : {}),
        })),
      ),
    [isSuper],
  );

  const handleSortModelChanged = useCallback(
    sortModel => {
      const [sortedColumn] = sortModel;

      if (!sortedColumn) return;

      onClick(COLUMNS.find(column => column.field === sortedColumn.colId).sortKey);
    },
    [onClick],
  );

  const handleContextMenuClick = useCallback(
    (event, { key, rowData }) => {
      if (key === 'edit') {
        onEdit(event, rowData);
      } else if (rowData.isParent) {
        onDeleteParent(event, rowData);
      } else {
        onDelete(event, rowData);
      }
    },
    [onEdit, onDelete, onDeleteParent],
  );

  return (
    <div className={style.grid}>
      <AgGridTable
        columnDefs={columnDefs}
        contextActionList={CONTEXT_ACTIONS}
        isFetching={loading}
        onContextMenuClick={isSuper ? handleContextMenuClick : undefined}
        onSortModelChanged={handleSortModelChanged}
        rowClassRules={rowClassRules}
        rowData={tags}
        style={gridStyle}
        serverSideSorting
        sortable
      />
    </div>
  );
};

TagTableComponent.propTypes = {
  isSuper: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteParent: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
};

export const TagTable = memo(TagTableComponent);
